/**
 * <stl-hotspot> — Shop the look floating-card hotspot (Hoamatkult-style popup).
 *
 * Desktop (>= 1000px): the card is positioned `position: absolute` next to the dot, inside the section, so it
 *   scrolls with the section. JS decides which side to open on (right if it fits, otherwise left).
 * Mobile (<= 999px): the card is portalled to <body> and slides up from the bottom as a sheet (with a backdrop),
 *   matching the Hoamatkult / Impact bottom-sheet behaviour, so it never overflows the viewport.
 *
 * One card open at a time per `name`. Closes on outside click / Escape / resize. No global side effects.
 */

if (!customElements.get('stl-hotspot')) {
  customElements.define(
    'stl-hotspot',
    class StlHotspot extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.stl-dot-button');
        this.card = this.querySelector('.stl-floating-card');

        if (!this.button || !this.card) {
          return;
        }

        this.isOpen = false;
        this.isSheet = false;
        this._placeholder = document.createComment('stl-card');
        this._onButtonClick = this.toggle.bind(this);
        this._onDocPointer = this._handleOutside.bind(this);
        this._onKeydown = this._handleKeydown.bind(this);
        this._onResize = this.close.bind(this);

        // The quick-add link opens the theme quick-buy drawer — slide our card away so it doesn't cover the drawer.
        this.quickAddTrigger = this.querySelector('.stl-card__action[aria-controls]');
        this._onQuickAdd = this.close.bind(this);

        this.button.addEventListener('click', this._onButtonClick);
        if (this.quickAddTrigger) {
          this.quickAddTrigger.addEventListener('click', this._onQuickAdd);
        }

        if (this.hasAttribute('open-on-load') && window.matchMedia('(min-width: 1000px)').matches) {
          requestAnimationFrame(() => this.open());
        }
      }

      disconnectedCallback() {
        this.close();
        this.button?.removeEventListener('click', this._onButtonClick);
        this.quickAddTrigger?.removeEventListener('click', this._onQuickAdd);

        // If we are removed while the sheet is open, close() cannot put the card back — its placeholder went away
        // with us — so the card would be orphaned in <body>. Visible in the theme editor when a section is removed.
        if (this.card?.parentNode === document.body) {
          this.card.remove();
        }
        this._backdrop?.remove();
      }

      toggle(event) {
        if (event) {
          event.preventDefault();
        }
        this.isOpen ? this.close() : this.open();
      }

      open() {
        const group = this.getAttribute('name');
        if (group) {
          document.querySelectorAll(`stl-hotspot[name="${group}"]`).forEach((el) => {
            if (el !== this && typeof el.close === 'function') {
              el.close();
            }
          });
        }

        this.isOpen = true;
        this.isSheet = window.matchMedia('(max-width: 999px)').matches;

        if (this.isSheet) {
          // Portal to <body> so the fixed bottom sheet is relative to the viewport (escapes transformed ancestors).
          if (this.card.parentNode !== document.body) {
            this.insertBefore(this._placeholder, this.card);
            document.body.appendChild(this.card);
          }
          this.card.classList.add('stl-floating-card--sheet');
          this._showBackdrop();
        } else {
          this._decideSide();
        }

        this.setAttribute('open', '');
        this.button.setAttribute('aria-expanded', 'true');

        requestAnimationFrame(() => {
          this.card.classList.add('is-open');
          document.addEventListener('pointerdown', this._onDocPointer);
          document.addEventListener('keydown', this._onKeydown);
          window.addEventListener('resize', this._onResize);
        });
      }

      close() {
        if (!this.isOpen) {
          return;
        }
        this.isOpen = false;
        this.removeAttribute('open');
        this.button.setAttribute('aria-expanded', 'false');
        this.card.classList.remove('is-open');

        document.removeEventListener('pointerdown', this._onDocPointer);
        document.removeEventListener('keydown', this._onKeydown);
        window.removeEventListener('resize', this._onResize);

        this._hideBackdrop();

        if (this.isSheet) {
          // Restore the card to the light DOM once the slide-down has finished.
          const card = this.card;
          const placeholder = this._placeholder;
          const restore = () => {
            card.removeEventListener('transitionend', restore);
            card.classList.remove('stl-floating-card--sheet');
            if (placeholder.parentNode) {
              placeholder.parentNode.insertBefore(card, placeholder);
              placeholder.remove();
            }
          };
          card.addEventListener('transitionend', restore, { once: true });
        }

        this.isSheet = false;
      }

      _handleOutside(event) {
        if (!this.contains(event.target) && !this.card.contains(event.target)) {
          this.close();
        }
      }

      _handleKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
          this.button.focus();
        }
      }

      _showBackdrop() {
        if (!this._backdrop) {
          this._backdrop = document.createElement('div');
          this._backdrop.className = 'stl-sheet-backdrop';
        }
        document.body.appendChild(this._backdrop);
        requestAnimationFrame(() => this._backdrop.classList.add('is-open'));
      }

      _hideBackdrop() {
        if (!this._backdrop || !this._backdrop.parentNode) {
          return;
        }
        const backdrop = this._backdrop;
        backdrop.classList.remove('is-open');
        const remove = () => {
          backdrop.removeEventListener('transitionend', remove);
          if (backdrop.parentNode) {
            backdrop.remove();
          }
        };
        backdrop.addEventListener('transitionend', remove, { once: true });
      }

      // Desktop only — prefer opening to the right; flip left when the card wouldn't fit there.
      _decideSide() {
        const dot = this.button.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;
        const gap = 16;
        const margin = 12;
        const cardWidth = this.card.offsetWidth || 320;

        const fitsRight = dot.right + gap + cardWidth <= viewportWidth - margin;
        const spaceLeft = dot.left;
        const spaceRight = viewportWidth - dot.right;

        this.toggleAttribute('reverse', !fitsRight && spaceLeft > spaceRight);
      }
    }
  );
}
